import React, { useMemo, useState } from 'react';
import { useMarketContext } from '../context/MarketDataContext';
import BreakoutTables, { ConvergenceTable } from '../components/BreakoutTables';
import ShortTickers from '../components/ShortTickers';
import ShortStrategies from '../components/ShortStrategies';

const SortIcon = ({ direction }) => (
  <svg className="w-3 h-3 inline-block ml-1" viewBox="0 0 20 20" fill="currentColor">
    {direction === 'asc' ? (
      <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
    ) : (
      <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
    )}
  </svg>
);

function formatTime(unixMs) {
  if (!unixMs) return null;
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York'
  });
}

function HistoryTable({ entries, tickers }) {
  const [sortStack, setSortStack] = useState([{ col: 'change', dir: 'desc' }]);

  const rows = useMemo(() => {
    const cmpCol = (a, b, col, dir) => {
      let aVal, bVal;
      if (col === 'time') {
        aVal = a.entry_time || 0;
        bVal = b.entry_time || 0;
      } else {
        aVal = a.changePercent != null ? a.changePercent : (dir === 'asc' ? 999 : -999);
        bVal = b.changePercent != null ? b.changePercent : (dir === 'asc' ? 999 : -999);
      }
      return dir === 'asc' ? aVal - bVal : bVal - aVal;
    };

    return entries
      .map((e) => {
        const live = tickers[e.symbol];
        const price = live ? live.price : e.price || e.entry_price || null;
        const changePercent = live ? live.changePercent : e.entry_change || null;
        return { ...e, livePrice: price, changePercent };
      })
      .sort((a, b) => {
        for (const { col, dir } of sortStack) {
          const cmp = cmpCol(a, b, col, dir);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
  }, [entries, tickers, sortStack]);

  const handleSort = (col) => {
    setSortStack((prev) => {
      const idx = prev.findIndex((s) => s.col === col);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = { col, dir: updated[idx].dir === 'asc' ? 'desc' : 'asc' };
        return updated;
      }
      return [...prev, { col, dir: 'asc' }];
    });
  };

  const clearSort = () => setSortStack([]);
  const sortIndex = (col) => sortStack.findIndex((s) => s.col === col);
  const sortDirFor = (col) => { const e = sortStack.find((s) => s.col === col); return e ? e.dir : null; };

  const openTradingView = (symbol) => {
    window.open(
      `https://www.tradingview.com/chart/?symbol=${symbol}&interval=1`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden mb-6">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-300 uppercase tracking-wider">History (Top 5 Gainers)</h2>
          <div className="flex items-center gap-3">
            {sortStack.length > 1 && (
              <button onClick={clearSort} className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors uppercase tracking-wider">Clear sort</button>
            )}
            <span className="text-xs text-gray-500">
              {rows.length} {rows.length === 1 ? 'ticker' : 'tickers'}
            </span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-900 z-10">
            <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 sm:px-6 py-3">Ticker</th>
              <th className="px-4 sm:px-6 py-3 text-right">Price</th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortIndex('change') !== -1 ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('change')}
              >
                Change %
                {sortDirFor('change') && <SortIcon direction={sortDirFor('change')} />}
                {sortStack.length > 1 && sortIndex('change') !== -1 && <span className="ml-1 text-[9px] text-gray-500">{sortIndex('change') + 1}</span>}
              </th>
              <th
                className={`px-4 sm:px-6 py-3 text-right cursor-pointer select-none hover:text-gray-300 transition-colors ${sortIndex('time') !== -1 ? 'text-gray-300' : ''}`}
                onClick={() => handleSort('time')}
              >
                Time
                {sortDirFor('time') && <SortIcon direction={sortDirFor('time')} />}
                {sortStack.length > 1 && sortIndex('time') !== -1 && <span className="ml-1 text-[9px] text-gray-500">{sortIndex('time') + 1}</span>}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 sm:px-6 py-12 text-center">
                  <span className="text-sm text-gray-500">No history yet</span>
                </td>
              </tr>
            ) : rows.map((row) => {
                const hasPrice = row.livePrice != null;
                const isPositive = row.changePercent != null && row.changePercent >= 0;
                const timeStr = formatTime(row.entry_time);
                return (
                  <tr
                    key={row.symbol}
                    onClick={() => openTradingView(row.symbol)}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-sm font-semibold text-gray-200">{row.symbol}</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {hasPrice ? (
                        <span className="text-sm text-gray-300 font-medium tabular-nums">
                          ${row.livePrice.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {row.changePercent != null ? (
                        <span
                          className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${
                            isPositive ? 'text-green-400' : 'text-red-400'
                          }`}
                        >
                          {isPositive ? (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
                            </svg>
                          )}
                          {isPositive ? '+' : ''}{row.changePercent.toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      {timeStr ? (
                        <span className="text-sm text-gray-400 tabular-nums">{timeStr}</span>
                      ) : (
                        <span className="text-sm text-gray-600 tabular-nums">&mdash;</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ShortAndLong() {
  const { tickers, shortSymbols, breakoutLongs, breakoutShorts, top5History, convergenceSignals } = useMarketContext();

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Short & Long</h1>
      </div>

      <HistoryTable entries={top5History} tickers={tickers} />

      <BreakoutTables
        longs={breakoutLongs}
        shorts={breakoutShorts}
        tickers={tickers}
      />

      <ConvergenceTable signals={convergenceSignals} tickers={tickers} />

      <ShortStrategies />

      <ShortTickers tickers={tickers} shortSymbols={shortSymbols} />
    </div>
  );
}
